import { ExclamationTriangleIcon as ExclamationTriangleIconOutline } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon as ExclamationTriangleIconSolid } from '@heroicons/react/24/solid';
import { Modal, Button } from '@components/ui';
import { cn } from '@lib/utils/cn';
import type { CertificateExpiryStatus } from '../types/certificate.types';
import { formatExpiryRelative } from '../utils/certificateExpiry';

/**
 * URL de la mesa de ayuda de soporte. Configurable por entorno -- NUNCA
 * hardcodear en el codigo mas alla de este fallback. Debe estar configurada
 * de verdad en .env.qa/.env.example (ver 00-loop-prompt.md punto 4d) para no
 * repetir el bug de VITE_SIGNATURE_REQUEST_EMAIL en ProfileIncompleteModal.tsx.
 */
const SUPPORT_URL = import.meta.env.VITE_SUPPORT_URL || 'https://support.hypernovalabs.com/';

type ActionableExpiryStatus = Extract<CertificateExpiryStatus, 'yellow' | 'red'>;

interface CertificateExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** El modal solo dispara en amarillo o rojo -- nunca en verde. */
  status: ActionableExpiryStatus;
  /** Fecha mas proxima de vencimiento, para mostrar "vence en N dias"/"vencido hace N dias". */
  nearestExpiresAt: Date | null;
}

const SUPPORT_LINK = (
  <a
    href={SUPPORT_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
    data-testid="certificate-expiry-modal-support-link"
  >
    {SUPPORT_URL}
  </a>
);

// Mensajes decididos y aprobados -- usar tal cual, sin redactar de nuevo
// (00-loop-prompt.md puntos 4e/4f). El enlace de la mesa de ayuda va
// embebido dentro del texto (donde antes decia "(<url>)"), como link
// clickeable que abre una pestana nueva -- sin boton "Contactar ahora"
// aparte.
const COPY: Record<ActionableExpiryStatus, { title: string; body: JSX.Element }> = {
  yellow: {
    title: 'Certificado próximo a expirar',
    body: (
      <>
        Aviso importante: Su certificado de firma electrónica está próximo a expirar. Para
        evitar interrupciones en el uso de nuestros servicios, por favor contacte a nuestra
        mesa de ayuda ({SUPPORT_LINK}) para gestionar su renovación.
      </>
    ),
  },
  red: {
    title: 'Certificado de firma expirado',
    body: (
      <>
        Su certificado de firma electrónica ha expirado. Sus facturas pueden estar fallando en
        este momento. Por favor contacte a nuestra mesa de ayuda ({SUPPORT_LINK}) de inmediato
        para gestionar su renovación.
      </>
    ),
  },
};

/**
 * Pop-up de aviso de vencimiento de certificado. Dispara en Amarillo (<=90
 * dias) o Rojo (ya vencido), nunca en Verde. Sin boton X -- solo "Cerrar" en
 * el pie, para no duplicar la salida (Modal ya cierra tambien al hacer click
 * fuera del panel via Headless UI Dialog.onClose). Sin logica de posponer:
 * vuelve a aparecer en el siguiente login mientras el estado siga activo.
 */
export function CertificateExpiryModal({ isOpen, onClose, status, nearestExpiresAt }: CertificateExpiryModalProps) {
  const copy = COPY[status];
  const Icon = status === 'red' ? ExclamationTriangleIconSolid : ExclamationTriangleIconOutline;
  const relative = formatExpiryRelative(status, nearestExpiresAt);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      size="sm"
      data-testid="certificate-expiry-modal"
      icon={
        <Icon
          className={cn('h-6 w-6', status === 'red' ? 'text-danger-600' : 'text-warning-500')}
        />
      }
      title={copy.title}
      footer={
        <Button
          variant={status === 'red' ? 'danger' : 'outline'}
          onClick={onClose}
          className="w-full"
          data-testid="certificate-expiry-modal-close"
        >
          Cerrar
        </Button>
      }
    >
      <p className="text-sm leading-relaxed text-secondary-700 dark:text-secondary-300">
        {copy.body}
      </p>
      {relative && (
        <p
          className={cn(
            'mt-3 text-sm font-semibold',
            status === 'red' ? 'text-danger-600 dark:text-danger-400' : 'text-warning-600 dark:text-warning-400'
          )}
          data-testid="certificate-expiry-modal-relative"
        >
          {relative}
        </p>
      )}
    </Modal>
  );
}
